import React from "react";
import {
  Stack,
  Box,
  Typography,
  Paper,
  Skeleton,
  Chip,
  LinearProgress,
  alpha,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PhonelinkRingIcon from "@mui/icons-material/PhonelinkRing";

import InfoButton from "../Clinical/InfoButton";
import {
  formatNumber,
  ENGAGEMENT_COLORS,
} from "../../constants/ceoDashboard";

/** Orden de display de los buckets de visita */
const ENGAGEMENT_ORDER = [
  "<3 Months",
  "3 Months",
  "6 Months",
  "9 Months",
  "12+ Months",
];

/** Configuracion de las barras de contact availability */
const CONTACT_ROWS = [
  { key: "hasEmail", pctKey: "hasEmailPct", label: "Email", color: "#3B82F6", icon: <EmailIcon fontSize="small" /> },
  { key: "hasPhone", pctKey: "hasPhonePct", label: "Phone", color: "#10B981", icon: <PhoneIcon fontSize="small" /> },
  { key: "hasMobile", pctKey: "hasMobilePct", label: "Mobile", color: "#F59E0B", icon: <PhonelinkRingIcon fontSize="small" /> },
];

/**
 * Panel 7 - Engagement & Growth (v2)
 * 3 columnas: Visit Engagement | New Members | Contact Availability
 *
 * v2 changes:
 * - Visit Engagement shows dual comparison bars (Availity vs Sante+)
 * - New Members uses locationName instead of centerName/locationKey
 */
export default function PanelEngagement({ data, loading, helpContent }) {
  // ── Styles comunes para inner cards ──────────────────────────
  const innerCardSx = {
    elevation: 0,
    p: 2.5,
    borderRadius: 3,
    bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),
    border: "1px solid",
    borderColor: "divider",
  };

  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Header skeleton */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" width={36} height={36} animation="wave" />
          <Skeleton variant="text" width={200} height={32} animation="wave" />
        </Stack>

        {/* 3 column skeleton */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
          }}
        >
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={280}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
          ))}
        </Box>
      </Paper>
    );
  }

  if (!data) return null;

  const { visitEngagement, newMembers, contactAvailability } = data;
  const total = visitEngagement?.total || 1;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: "info.main",
            color: "white",
          }}
        >
          <GroupsIcon fontSize="small" />
        </Box>
        <Typography variant="h6" fontWeight={700}>
          Engagement & Growth
        </Typography>
        <InfoButton helpContent={helpContent} />
      </Stack>

      {/* ── 3-Column Grid ──────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
        }}
      >
        {/* ── LEFT: Visit Engagement ────────────────────────────── */}
        <Paper elevation={0} sx={innerCardSx}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Visit Engagement
          </Typography>

          {/* Dual comparison bars: Availity vs Sante+ */}
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {/* Availity bar */}
            <Stack spacing={0.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  Availity
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(visitEngagement?.avalityVisited ?? 0)} patients
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={visitEngagement?.avalityEngagementRate ?? 0}
                    sx={{
                      height: 20,
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.action.hover, 0.4),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 2,
                        bgcolor: "#3B82F6",
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#3B82F6", minWidth: 45, textAlign: "right" }}>
                  {visitEngagement?.avalityEngagementRate ?? 0}%
                </Typography>
              </Stack>
            </Stack>

            {/* Sante+ bar */}
            <Stack spacing={0.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  Sante+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(visitEngagement?.spVisited ?? 0)} patients
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={visitEngagement?.spEngagementRate ?? 0}
                    sx={{
                      height: 20,
                      borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.action.hover, 0.4),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 2,
                        bgcolor: "#10B981",
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#10B981", minWidth: 45, textAlign: "right" }}>
                  {visitEngagement?.spEngagementRate ?? 0}%
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* SP boost caption */}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            {formatNumber(visitEngagement?.spBoostCount ?? 0)} additional visits captured by Sante Plus
          </Typography>

          {/* Horizontal stacked bars breakdown */}
          <Stack spacing={1}>
            {ENGAGEMENT_ORDER.map((bucket) => {
              const count = visitEngagement?.breakdown?.[bucket] || 0;
              const pct = (count / total) * 100;

              return (
                <Stack key={bucket} spacing={0.25}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      {bucket}
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {formatNumber(count)}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      width: "100%",
                      height: 16,
                      bgcolor: (theme) =>
                        alpha(theme.palette.action.hover, 0.4),
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${pct}%`,
                        height: "100%",
                        bgcolor: ENGAGEMENT_COLORS[bucket],
                        borderRadius: 1,
                        transition: "width 600ms ease",
                      }}
                    />
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Paper>

        {/* ── CENTER: New Members ───────────────────────────────── */}
        <Paper elevation={0} sx={innerCardSx}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            New Members
          </Typography>

          {/* Large count */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                lineHeight: 1.2,
              }}
            >
              {formatNumber(newMembers?.total ?? 0)}
            </Typography>
            <Chip
              label={`+${newMembers?.growthRate ?? 0}%`}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                color: "success.main",
                fontSize: "0.75rem",
              }}
            />
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            new members
          </Typography>

          {/* Top 5 locations */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1, display: "block" }}
          >
            Top Locations
          </Typography>
          <Stack spacing={0.75}>
            {(newMembers?.byLocation || []).slice(0, 5).map((loc, idx) => (
              <Stack
                key={loc.locationName || idx}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  py: 0.5,
                  px: 1,
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography variant="body2">{loc.locationName}</Typography>
                <Chip
                  label={formatNumber(loc.count)}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: "0.75rem", height: 24 }}
                />
              </Stack>
            ))}
          </Stack>
        </Paper>

        {/* ── RIGHT: Contact Availability ──────────────────────── */}
        <Paper elevation={0} sx={innerCardSx}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Contact Info
          </Typography>

          <Stack spacing={2.5}>
            {CONTACT_ROWS.map((row) => {
              const count = contactAvailability?.[row.key] ?? 0;
              const pct = contactAvailability?.[row.pctKey] ?? 0;

              return (
                <Stack key={row.key} spacing={0.75}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Box sx={{ color: row.color, display: "flex" }}>
                      {row.icon}
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {row.label}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: (theme) =>
                        alpha(theme.palette.action.hover, 0.4),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 5,
                        bgcolor: row.color,
                      },
                    }}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: row.color }}
                    >
                      {pct}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(count)} members
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      </Box>
    </Paper>
  );
}
